import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { analytics } from "../firebaseConfig";
import { logEvent } from "firebase/analytics";

const PrivacyPolicy = () => {
    useEffect(() => {
        logEvent(analytics, 'privacy_policy_page');
    }, [])
    return (
        <div className="max-w-3xl mx-auto px-4 py-8 text-gray-800 dark:text-gray-200">
            <Helmet>
                <title>Privacy Policy | AI Contract Manager</title>
                <meta name="description" content="Read how Clairvoyant Inc. collects, stores, and protects your data when using our AI Contract Management App." />
                <link rel="canonical" href="https://aicontractmanager.com/privacy" />
            </Helmet>
            <h1 className="text-3xl font-bold mb-6 text-center">Privacy Policy</h1>
            <p className="text-sm mb-6 text-center">Effective Date: April 2, 2025</p>

            <p className="mb-4">
                Thank you for using our AI Contract Management App ("App", "we", "our", or "us"). This App is owned and operated by <strong>Clairvoyant Inc.</strong> ("Company"). Your privacy is important to us, and we are committed to protecting the personal information you share with us.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-2">1. Information We Collect</h2>
            <p className="mb-2"><strong>a. Personal Information:</strong> Name, email address, organization name, and login credentials.</p>
            <p className="mb-2"><strong>b. Contract Data:</strong> Uploaded documents, extracted summaries, and AI-derived metadata.</p>
            <p className="mb-2"><strong>c. Usage Data:</strong> Pages visited, device/browser info, IP address, location data.</p>
            <p className="mb-4"><strong>d. Cookies:</strong> Used for tracking, performance, and preferences. Manage via browser settings.</p>

            <h2 className="text-xl font-semibold mt-8 mb-2">2. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-1 mb-4">
                <li>To provide and maintain the AI contract management service.</li>
                <li>To extract and summarize key contract terms.</li>
                <li>To personalize and improve the user experience.</li>
                <li>To fulfill legal obligations.</li>
                <li>We do <strong>not</strong> sell personal data. Aggregated, anonymized data may be used for improvement.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-2">3. Legal Basis for Processing (GDPR)</h2>
            <ul className="list-disc list-inside space-y-1 mb-4">
                <li>Consent (when you sign up)</li>
                <li>Contractual necessity</li>
                <li>Legal obligation</li>
                <li>Legitimate interests (service improvement, fraud prevention)</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-2">4. Your Rights</h2>
            <ul className="list-disc list-inside space-y-1 mb-4">
                <li>Access, correct, delete your data</li>
                <li>Restrict or object to processing</li>
                <li>Withdraw consent at any time</li>
                <li>Request data portability</li>
                <li>Opt out of data sharing/sale (CCPA)</li>
            </ul>
            <p className="mb-4">To exercise your rights, email us at <a href="mailto:support@aicontractmanager.com" className="text-blue-600 dark:text-blue-400 underline">support@aicontractmanager.com</a></p>

            <h2 className="text-xl font-semibold mt-8 mb-2">5. Data Sharing</h2>
            <ul className="list-disc list-inside space-y-1 mb-4">
                <li>Service providers: Firebase, OpenAI, Stripe</li>
                <li>Legal requests: law enforcement, court orders</li>
                <li>GDPR-compliant processors with proper contracts</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-2">6. International Transfers (GDPR)</h2>
            <p className="mb-4">Your data may be processed in the U.S. or other jurisdictions. We use appropriate safeguards like Standard Contractual Clauses when required.</p>

            <h2 className="text-xl font-semibold mt-8 mb-2">7. Data Security</h2>
            <p className="mb-4">We apply encryption, secure hosting, and access control to keep your information safe.</p>

            <h2 className="text-xl font-semibold mt-8 mb-2">8. Data Retention</h2>
            <p className="mb-4">We retain your data as long as needed to provide services, resolve disputes, and comply with legal obligations. You may request deletion at any time.</p>

            <h2 className="text-xl font-semibold mt-8 mb-2">9. Children’s Privacy</h2>
            <p className="mb-4">We do not knowingly collect personal data from children under 13. The App is not intended for use by minors.</p>

            <h2 className="text-xl font-semibold mt-8 mb-2">10. Changes to This Policy</h2>
            <p className="mb-4">We may revise this Privacy Policy. When we do, we’ll update the effective date. Continued use constitutes acceptance.</p>

            <h2 className="text-xl font-semibold mt-8 mb-2">11. Contact Us</h2>
            <p className="mb-4">
                📧 <a href="mailto:support@aicontractmanager.com" className="text-blue-600 dark:text-blue-400 underline">support@aicontractmanager.com</a><br />
                📍 Clairvoyant Inc., Los Angeles, CA
            </p>
            <p className="mb-4">To opt out of the sale of personal information (under CCPA), email with the subject: <strong>“Do Not Sell My Personal Info”</strong>.</p>
        </div>
    );
};

export default PrivacyPolicy;
